import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { Schema } from "@bw/shared/types/generated";
import { formatDate } from "@bw/shared/utils";

import { safeActorLabel, type FacilityRead } from "./facilityDetailFormatters";

type AuditRead = Schema["AuditQueryResponse"];

export function FacilityActivityPanel({
  activity,
}: {
  activity: FacilityRead<AuditRead>;
}) {
  const entries = activity.data?.entries ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facility-activity-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}
    >
      <Stack spacing={2}>
        <Typography
          id="facility-activity-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Facility activity
        </Typography>
        {activity.state === "loading" ? (
          <Box
            role="status"
            aria-label="Loading facility activity"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : activity.state === "error" ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button color="inherit" size="small" onClick={activity.onRetry}>
                Retry
              </Button>
            }
          >
            Facility activity could not be loaded. Try again.
          </Alert>
        ) : entries.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No facility-level actions recorded yet.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {entries.map((entry) => (
              <Stack
                key={entry.entry_id}
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ justifyContent: "space-between", py: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                  >
                    {entry.action}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    by{" "}
                    {safeActorLabel(entry.actor_display_name, entry.actor_id)}
                  </Typography>
                </Box>
                <Typography
                  color="text.secondary"
                  variant="caption"
                  sx={{ flexShrink: 0 }}
                >
                  {formatDate(entry.created_at)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
